import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { AuthService } from './auth.service';
import { HostConfigService } from './host-config.service';
import { CLAVE_SESION_CERRADA_INACTIVIDAD, INACTIVIDAD } from '@core/constants/inactividad.constants';

@Injectable()
export class InactividadService {
  public readonly advertenciaVisible;
  public readonly segundosRestantes;

  private readonly idle = inject(Idle);
  private readonly authService = inject(AuthService);
  private readonly hostConfigService = inject(HostConfigService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _advertenciaVisible = signal(false);
  private readonly _segundosRestantes = signal<number>(INACTIVIDAD.SEGUNDOS_ADVERTENCIA);
  private segundosAdvertencia: number = INACTIVIDAD.SEGUNDOS_ADVERTENCIA;
  private configurado = false;

  constructor() {
    this.advertenciaVisible = this._advertenciaVisible.asReadonly();
    this.segundosRestantes = this._segundosRestantes.asReadonly();
  }

  public consumirCierrePorInactividad(): boolean {
    if (!localStorage.getItem(CLAVE_SESION_CERRADA_INACTIVIDAD)) {
      return false;
    }
    localStorage.removeItem(CLAVE_SESION_CERRADA_INACTIVIDAD);
    return true;
  }

  public iniciarVigilancia(): void {
    if (!this.configurado) {
      this.configurar();
    }
    if (!this.idle.isRunning()) {
      this.vigilar();
    }
  }

  public detenerVigilancia(): void {
    this.idle.stop();
    this._advertenciaVisible.set(false);
  }

  public reiniciar(): void {
    this._advertenciaVisible.set(false);
    this.vigilar();
  }

  public cerrarSesionManual(): void {
    this._advertenciaVisible.set(false);
    this.idle.stop();
    this.authService.cerrarSesion();
  }

  private vigilar(): void {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
  }

  private configurar(): void {
    const config = this.hostConfigService.getInactividadConfig();
    this.segundosAdvertencia = config.segundosAdvertencia;
    this._segundosRestantes.set(config.segundosAdvertencia);

    this.idle.setIdle(config.segundosInactividad);
    this.idle.setTimeout(config.segundosAdvertencia);

    this.idle.onIdleStart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._segundosRestantes.set(this.segundosAdvertencia);
        this._advertenciaVisible.set(true);
        this.idle.clearInterrupts();
      });

    this.idle.onIdleEnd
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._advertenciaVisible.set(false));

    this.idle.onTimeoutWarning
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((segundos: number) => this._segundosRestantes.set(segundos));

    this.idle.onTimeout
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cerrarPorInactividad());

    this.configurado = true;
  }

  private cerrarPorInactividad(): void {
    this._advertenciaVisible.set(false);
    this.idle.stop();
    localStorage.setItem(CLAVE_SESION_CERRADA_INACTIVIDAD, '1');
    this.authService.cerrarSesion();
  }
}
